import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Skull, FlaskConical, Diamond, Zap, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Send } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";
import { getTeamComparison, getRaids, sendTeamBattleDiscord } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TEAM_COLORS = { Monday: "#3b82f6", Thursday: "#ef4444" };

function GradeRing({ grade, label, color }) {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (grade / 100) * circumference;
  const gradeColor = grade >= 80 ? "#1eff00" : grade >= 60 ? "#0070dd" : grade >= 40 ? "#ff8800" : "#ff2222";

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-28 h-28">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#1e293b" strokeWidth="6" />
          <circle cx="50" cy="50" r={radius} fill="none" stroke={gradeColor} strokeWidth="6"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease-out" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold font-mono" style={{ color: gradeColor }}>{grade}</span>
          <span className="text-[10px] text-muted-foreground uppercase">Grade</span>
        </div>
      </div>
      <span className="text-sm font-bold" style={{ color }}>{label}</span>
    </div>
  );
}

function StatCompare({ label, valueA, valueB, icon: Icon, suffix = "", higherIsBetter = true }) {
  const a = typeof valueA === "number" ? valueA : 0;
  const b = typeof valueB === "number" ? valueB : 0;
  const winnerA = higherIsBetter ? a > b : a < b;
  const winnerB = higherIsBetter ? b > a : b < a;

  // Use down arrow for "lower is better" metrics, up arrow for "higher is better"
  const ArrowIcon = higherIsBetter ? ChevronUp : ChevronDown;

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-3 border-b border-border/20" data-testid={`stat-compare-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`text-right ${winnerA ? "text-green-400 font-bold" : ""}`}>
        <span className="text-lg font-mono">
          {typeof valueA === "number" ? valueA.toLocaleString() : valueA}{suffix}
        </span>
        {winnerA && <ArrowIcon className="h-4 w-4 text-green-500 inline ml-1" />}
      </div>
      <div className="flex flex-col items-center gap-0.5 min-w-[120px]">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <div className={`text-left ${winnerB ? "text-green-400 font-bold" : ""}`}>
        <span className="text-lg font-mono">
          {typeof valueB === "number" ? valueB.toLocaleString() : valueB}{suffix}
        </span>
        {winnerB && <ArrowIcon className="h-4 w-4 text-green-500 inline ml-1" />}
      </div>
    </div>
  );
}

function BossTimesChart({ bossTimesA, bossTimesB, teamA, teamB }) {
  const allBosses = new Set([...Object.keys(bossTimesA || {}), ...Object.keys(bossTimesB || {})]);
  const chartData = [...allBosses].map((boss) => ({
    boss: boss.length > 15 ? boss.substring(0, 15) + "..." : boss,
    [teamA]: bossTimesA?.[boss] || 0,
    [teamB]: bossTimesB?.[boss] || 0,
  }));

  if (chartData.length === 0) return null;

  return (
    <div className="stat-card p-4 mt-4" data-testid="boss-times-chart">
      <h3 className="heading text-base font-semibold uppercase tracking-tight mb-3">Best Kill Times (seconds)</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="boss" tick={{ fontSize: 10, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
          <RTooltip contentStyle={{ background: "#0f1623", border: "1px solid #1e293b", borderRadius: 4, fontSize: 12 }} />
          <Legend />
          <Bar dataKey={teamA} fill={TEAM_COLORS.Monday} radius={[3, 3, 0, 0]} maxBarSize={30} />
          <Bar dataKey={teamB} fill={TEAM_COLORS.Thursday} radius={[3, 3, 0, 0]} maxBarSize={30} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function TeamComparison() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mondayRaids, setMondayRaids] = useState([]);
  const [thursdayRaids, setThursdayRaids] = useState([]);
  const [selectedMonday, setSelectedMonday] = useState("all");
  const [selectedThursday, setSelectedThursday] = useState("all");
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadRaids = async () => {
      try {
        const [mr, tr] = await Promise.all([getRaids("Monday"), getRaids("Thursday")]);
        setMondayRaids(mr);
        setThursdayRaids(tr);
      } catch (e) { console.error("Failed to load team raids:", e); }
    };
    loadRaids();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const raidA = selectedMonday === "all" ? undefined : selectedMonday;
        const raidB = selectedThursday === "all" ? undefined : selectedThursday;
        const res = await getTeamComparison("Monday", "Thursday", raidA, raidB);
        setData(res);
      } catch (e) {
        toast.error("Failed to load team comparison");
      }
      setLoading(false);
    };
    load();
  }, [selectedMonday, selectedThursday]);

  if (loading && !data) {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const a = data.team_a;
  const b = data.team_b;

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="team-comparison-page">
      <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-2">
        Team Battle
      </h1>
      <p className="text-muted-foreground mb-6">Monday vs Thursday — who raids better?</p>

      {/* Raid Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider mb-1 block" style={{ color: TEAM_COLORS.Monday }}>
            Monday Raid
          </label>
          <Select value={selectedMonday} onValueChange={setSelectedMonday}>
            <SelectTrigger data-testid="monday-raid-selector" className="bg-card/50 text-sm h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Raids (Overall)</SelectItem>
              {mondayRaids.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.raid_name} — {r.date}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider mb-1 block" style={{ color: TEAM_COLORS.Thursday }}>
            Thursday Raid
          </label>
          <Select value={selectedThursday} onValueChange={setSelectedThursday}>
            <SelectTrigger data-testid="thursday-raid-selector" className="bg-card/50 text-sm h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Raids (Overall)</SelectItem>
              {thursdayRaids.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.raid_name} — {r.date}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grade Circles */}
      <div className="flex justify-center items-end gap-12 mb-8">
        <GradeRing grade={Math.round(a.grade)} label="Monday" color={TEAM_COLORS.Monday} />
        <div className="text-3xl font-bold text-muted-foreground mb-8">VS</div>
        <GradeRing grade={Math.round(b.grade)} label="Thursday" color={TEAM_COLORS.Thursday} />
      </div>

      {/* Head to Head Stats */}
      <Card className="bg-card/50 border-border mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center mb-4">
            <h3 className="heading text-base font-semibold uppercase tracking-tight text-right" style={{ color: TEAM_COLORS.Monday }}>Monday</h3>
            <span className="text-xs font-mono uppercase text-muted-foreground">Metric</span>
            <h3 className="heading text-base font-semibold uppercase tracking-tight" style={{ color: TEAM_COLORS.Thursday }}>Thursday</h3>
          </div>

          <StatCompare label="Wipes" icon={Skull} valueA={a.wipes} valueB={b.wipes} higherIsBetter={false} />
          <StatCompare label="Avg DPS" icon={TrendingUp} valueA={a.avg_dps} valueB={b.avg_dps} />
          <StatCompare label="Avg HPS" icon={TrendingUp} valueA={a.avg_hps} valueB={b.avg_hps} />
          <StatCompare label="Deaths" icon={Skull} valueA={a.total_deaths} valueB={b.total_deaths} higherIsBetter={false} />
          <StatCompare label="Interrupts" icon={Zap} valueA={a.total_interrupts} valueB={b.total_interrupts} />
          <StatCompare label="Consumables" icon={FlaskConical} valueA={a.consumables_used} valueB={b.consumables_used} />
          <StatCompare label="Enchant %" icon={Zap} valueA={a.enchant_pct} valueB={b.enchant_pct} suffix="%" />
          <StatCompare label="Gem %" icon={Diamond} valueA={a.gem_pct} valueB={b.gem_pct} suffix="%" />
        </CardContent>
      </Card>

      {/* Boss Kill Time Comparison — 25 Man */}
      {(Object.keys(a.boss_best_times_25 || {}).length > 0 || Object.keys(b.boss_best_times_25 || {}).length > 0) && (
        <>
          <h3 className="heading text-base font-semibold uppercase tracking-tight mt-6 mb-3">Best Kill Times — 25 Man</h3>
          <BossTimesChart
            bossTimesA={a.boss_best_times_25 || {}}
            bossTimesB={b.boss_best_times_25 || {}}
            teamA="Monday"
            teamB="Thursday"
          />
        </>
      )}

      {/* Boss Kill Time Comparison — 10 Man */}
      {(Object.keys(a.boss_best_times_10 || {}).length > 0 || Object.keys(b.boss_best_times_10 || {}).length > 0) && (
        <>
          <h3 className="heading text-base font-semibold uppercase tracking-tight mt-6 mb-3">Best Kill Times — 10 Man</h3>
          <BossTimesChart
            bossTimesA={a.boss_best_times_10 || {}}
            bossTimesB={b.boss_best_times_10 || {}}
            teamA="Monday"
            teamB="Thursday"
          />
        </>
      )}

      {/* Grading breakdown */}
      <div className="mt-6 stat-card p-4">
        <h3 className="heading text-base font-semibold uppercase tracking-tight mb-2">Grade Breakdown</h3>
        <p className="text-xs text-muted-foreground mb-3">Internal grading: Kill Rate (30%) + Enchants (20%) + Speed (25%) + Consumables (15%) + Gems (10%)</p>
        <div className="grid grid-cols-2 gap-3">
          <GradeBar label="Monday Grade" value={a.grade} color={TEAM_COLORS.Monday} />
          <GradeBar label="Thursday Grade" value={b.grade} color={TEAM_COLORS.Thursday} />
        </div>
      </div>
      {/* Discord Send */}
      <div className="mt-6 stat-card p-4" data-testid="discord-team-battle">
        <h3 className="heading text-base font-semibold uppercase tracking-tight mb-3">Share to Discord</h3>
        <div className="flex gap-2">
          <Input data-testid="team-battle-webhook-input"
            placeholder="Discord Webhook URL"
            value={webhookUrl}
            onChange={(e) => { setWebhookUrl(e.target.value); localStorage.setItem("discord_webhook_url", e.target.value); }}
            className="bg-background/50 text-sm h-8 flex-1" />
          <Button data-testid="team-battle-discord-send" size="sm" disabled={!webhookUrl || sending}
            onClick={async () => {
              setSending(true);
              try {
                await sendTeamBattleDiscord(webhookUrl, "Monday", "Thursday");
                toast.success("Team Battle sent to Discord!");
              } catch { toast.error("Failed to send"); }
              setSending(false);
            }}
            className="gap-1.5 h-8">
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

function GradeBar({ label, value, color }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-mono uppercase" style={{ color }}>{label}</span>
        <span className="text-sm font-bold font-mono">{Math.round(value)}%</span>
      </div>
      <div className="h-3 bg-secondary/50 rounded-sm overflow-hidden">
        <div className="h-full rounded-sm transition-all duration-1000" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
